import React, {
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import IconButton from '@material-ui/core/IconButton';
import Truncate from 'react-truncate';
import UserAvatar from 'components/UserAvatar';
import ChatroomIcon from 'components/icons/Chatroom';
import {
  UserTag, UserInfo,
  UserDescription,
} from 'components/chat/ChatUserComponents';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';
import { selectLocale } from 'models/localization';
import { nameByLocale } from 'hooks/useNameByLocale';

const RootContainer = styled.div`
  height: 64px;
  padding: 10px 16px 10px 30px;
  display: flex;
  align-items: center;
  transition: background-color 0.2s ease;

  :hover {
    background-color: #F0F1F2;
  }

  @media (max-width: 960px) {
    padding: 40px 16px 30px 30px;
  }
`;

const UserInfoContainer = styled.div`
  flex: 1 1 auto;
  margin-left: 10px;
  width: 60%;
`;

const StyledChatroomIcon = styled(ChatroomIcon)`
  cursor: pointer;
`;

const ParticipantListItem = ({
  uuid,
  name,
  firstName,
  lastName,
  userTag,
  userDescription,
}) => {
  const { createPrivateChat } = useTwilioEventFunctions();
  const locale = useSelector(selectLocale);
  const localizedName = useMemo(() => nameByLocale(firstName, lastName, locale, name), [firstName, lastName, locale, name]);
  const onPrivateChatBtnClick = useCallback(async (e) => {
    e.stopPropagation();
    window.trackingEventToFirebase(null, 'Chat', 'OpenPrivateChatWindow', 'Participant_List_Click');
    await createPrivateChat(uuid);
  }, [uuid, createPrivateChat]);

  return (
    <RootContainer>
      <UserAvatar name={localizedName} />
      <UserInfoContainer>
        <UserInfo>
          {localizedName}
          {userTag && (
            <UserTag>{userTag}</UserTag>
          )}
        </UserInfo>
        {!!(userDescription && userDescription.length) && (
          <UserDescription>
            <Truncate lines={2} ellipsis="...">
              {userDescription}
            </Truncate>
          </UserDescription>
        )}
      </UserInfoContainer>
      <IconButton onClick={onPrivateChatBtnClick} disabled={!uuid}>
        <StyledChatroomIcon
          color="#484848"
          width="24px"
          height="24px"
        />
      </IconButton>
    </RootContainer>
  );
};

ParticipantListItem.propTypes = {
  uuid: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  userTag: PropTypes.string,
  userDescription: PropTypes.string,
};

ParticipantListItem.defaultProps = {
  userTag: '',
  userDescription: '',
};

export default ParticipantListItem;
